import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ScreenerPanel({ onResultsUpdate }) {
  const [config, setConfig] = useState(null);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState("09:20");
  const [enabled, setEnabled] = useState(true);

  const loadConfig = useCallback(async () => {
    try {
      const res = await api.get("/screener/config");
      setConfig(res.data);
      setSchedule(res.data.schedule || "09:20");
      setEnabled(res.data.enabled !== false);
    } catch { /* ok */ }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async (runNow = false) => {
    setSaving(true);
    try {
      const res = await api.post("/screener/config", {
        schedule, enabled, india: true, us: false, crypto: false, run_now: runNow,
      });
      toast.success(runNow ? `Screener ran — ${res.data.passed} passed` : "Schedule saved");
      if (runNow && onResultsUpdate) onResultsUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (running) return;
    setRunning(true);
    try {
      const res = await api.post("/screener/run");
      toast.success(`Screener complete — ${res.data.passed} symbol(s) passed`);
      if (onResultsUpdate) onResultsUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Screener failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="bg-surface-2 border-border rounded-sm h-full">
      <CardHeader className="pb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1 font-semibold">
          / scanner
        </div>
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          <Search className="w-4 h-4 text-brand" />
          Trend Screener
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Trend-gated screener: Close &gt; SMA(20) &gt; SMA(50) + RSI(14) &gt; 55,
          volume spike, volatility expansion, and a bullish candle.
        </p>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
              Daily at
            </span>
          </div>
          <Input
            type="time"
            value={schedule}
            onChange={e => setSchedule(e.target.value)}
            className="h-8 rounded-sm bg-surface-1 border-border text-xs w-28 font-mono"
          />
          <div className="flex items-center gap-2">
            <Label htmlFor="screener-toggle" className="text-[10px] text-muted-foreground">Auto</Label>
            <Switch
              id="screener-toggle"
              checked={enabled}
              onCheckedChange={setEnabled}
              className="scale-75"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={runNow}
            disabled={running}
            className="flex-1 h-9 rounded-sm bg-brand text-white hover:bg-brand/90 text-xs font-medium"
          >
            <RefreshCw className={`w-3 h-3 mr-1.5 ${running ? "animate-spin" : ""}`} />
            {running ? "Scanning..." : "Run Now"}
          </Button>
          <Button
            onClick={() => saveConfig(false)}
            disabled={saving}
            variant="outline"
            className="h-9 rounded-sm text-xs"
          >
            {saving ? "Saving..." : "Save Schedule"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
